import React, { useEffect, useState, memo, useCallback, useRef } from 'react';
import { ChevronDown } from 'lucide-react';

const HeroSection = memo(() => {
  const [scrollY, setScrollY] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        setScrollY(window.scrollY);
        rafRef.current = null;
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const scrollToAbout = useCallback(() => {
    const element = document.querySelector('#about');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  return (
    <section id="hero" className="relative min-h-screen overflow-hidden">
      {/* Background Image with Parallax */}
      <div 
        className="absolute inset-0 z-0 will-change-transform"
        style={{
          transform: `translateY(${scrollY * 0.3}px)`,
        }}
      >
        <img
          src="/assets/hero.webp"
          alt="Elegant Saree Collection"
          className="w-full h-full object-cover object-top"
          loading="eager"
          fetchpriority="high"
          style={{
            minHeight: '120vh',
          }}
        />
        {/* Gradient Overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/50" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-transparent to-background" />
      </div>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex flex-col justify-center px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pt-20">
        <div className="max-w-2xl">
          {/* Main Heading */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-foreground leading-tight mb-6 animate-fade-in-up" style={{ fontFamily: 'Faith, Playfair Display, serif' }}>
            Where Tradition<br />
            <span className="text-gold-gradient">Meets Elegance</span>
          </h1>

          {/* Description */}
          <p className="font-body text-base sm:text-lg text-foreground/70 max-w-lg mb-8 leading-relaxed animate-fade-in-up stagger-1 opacity-0">
            Discover exquisite handcrafted sarees that celebrate India&apos;s rich textile heritage. 
            Each piece tells a story of artistry, tradition, and timeless beauty.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up stagger-2 opacity-0">
            <a
              href="https://www.instagram.com/shachi_collections"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative px-8 py-4 bg-primary text-primary-foreground font-body font-medium text-lg overflow-hidden transition-all duration-300 hover:glow-gold-intense text-center"
            >
              <span className="relative z-10">Shop on Instagram</span>
              <div className="absolute inset-0 shimmer" />
            </a>
            <a
              href="https://wa.me/917619546174?text=Hello!%20I%20would%20like%20to%20inquire%20about%20your%20saree%20collection."
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 border border-primary/50 text-foreground font-body font-medium text-lg transition-all duration-300 hover:border-primary hover:bg-primary/10 text-center"
            >
              DM on WhatsApp
            </a>
          </div>
        </div>
      </div>

      {/* Scroll Indicator - hidden on mobile to prevent overlap with buttons */}
      <button
        onClick={scrollToAbout}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden sm:flex flex-col items-center gap-2 text-primary/60 hover:text-primary transition-colors cursor-pointer group"
      >
        <span className="font-elegant text-sm tracking-widest uppercase">Scroll</span>
        <ChevronDown className="w-6 h-6 animate-bounce" />
      </button>

      {/* Gold line decoration */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
    </section>
  );
});

HeroSection.displayName = 'HeroSection';

export default HeroSection;
